import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import mapboxgl from 'mapbox-gl';
import { SessionPub } from '../api/types';
import { MAPBOX_CONFIG } from '../config/api';
import PubRow from './PubRow';
import { getCrawlBounds } from '../utils/mapBounds';
import './Map.css';

// Set the access token
mapboxgl.accessToken = MAPBOX_CONFIG.ACCESS_TOKEN;

// Run the worker from mapbox's own prebuilt file (copied into public/ by the
// prestart/prebuild scripts). By default mapbox builds its worker from its own
// bundled source, but CRA's Babel pass rewrites that source to call helpers
// (e.g. _objectSpread) defined outside the worker, so the worker throws and the
// map never loads.
mapboxgl.workerUrl = `${process.env.PUBLIC_URL}/mapbox-gl-csp-worker.js`;

/** Padding around the crawl, and a zoom cap so a one-pub crawl isn't framed at street level. */
const FIT_OPTIONS = { padding: 50, maxZoom: 16 };

interface MapProps {
  pubs: SessionPub[];
  /** Pubs scanned on this device but not yet confirmed by the server */
  pendingPubIds?: string[];
}

/** A marker's popup body: mapbox owns the node, React renders into it. */
interface PopupSlot {
  pub: SessionPub;
  index: number;
  node: HTMLDivElement;
}

const NO_PENDING: string[] = [];

const Map: React.FC<MapProps> = ({ pubs, pendingPubIds = NO_PENDING }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const [mapError, setMapError] = useState<string | null>(null);
  const [popupSlots, setPopupSlots] = useState<PopupSlot[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  /** Whether the view has been framed on the crawl. Once it has, later updates
   *  (a scan, a rename) refresh the markers but leave the user's pan and zoom. */
  const framed = useRef(false);
  /** Read by the one-off init effect, which mustn't re-run when pubs change. */
  const initialPubs = useRef(pubs);

  // Create the map once, already framed on the crawl when the pubs are known —
  // no start at a fixed spot followed by a pan and zoom across to the crawl.
  useEffect(() => {
    if (!mapContainer.current) return;

    const bounds = getCrawlBounds(initialPubs.current);
    framed.current = bounds !== null;

    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        ...(bounds
          ? { bounds, fitBoundsOptions: FIT_OPTIONS }
          : { center: MAPBOX_CONFIG.DEFAULT_CENTER, zoom: MAPBOX_CONFIG.DEFAULT_ZOOM }),
        pitch: 0,
        style: MAPBOX_CONFIG.DEFAULT_STYLE,
        maxZoom: 18,
        config: {
          basemap: {
            showPlaceLabels: false,
            showPointOfInterestLabels: false,
            showRoadLabels: false,
            showTransitLabels: true
          }
        },
      });

      // Add navigation controls
      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

      // Markers go on once the style has loaded
      map.current.on('load', () => setMapLoaded(true));
    } catch (error) {
      console.error('Error initializing map:', error);
      setMapError('Failed to initialize map. Please check your Mapbox configuration.');
    }

    // Cleanup function
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      markers.current.forEach(marker => marker.remove());
      markers.current = [];
    };
  }, []);

  // Refresh markers whenever the pubs change, without rebuilding the map
  useEffect(() => {
    if (!mapLoaded || !map.current) return;

    // Clear existing markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    // Add markers for each pub
    const slots: PopupSlot[] = [];
    pubs.forEach((pub, index) => {
      const coordinates = [pub.location.lon, pub.location.lat] as [number, number];

      // Create marker element
      const pending = !pub.isComplete && pendingPubIds.includes(pub.id);
      const markerElement = document.createElement('div');
      markerElement.className = `pub-marker ${pub.isComplete ? 'completed' : pending ? 'pending' : 'incomplete'}`;
      markerElement.dataset.testid = 'map-marker';
      markerElement.dataset.completed = pub.isComplete ? 'true' : 'false';
      markerElement.dataset.pending = pending ? 'true' : 'false';
      markerElement.setAttribute('aria-label', pending ? `${pub.name} (pending sync)` : pub.name);
      markerElement.innerHTML = `
        <div class="marker-number">${index + 1}</div>
        <div class="marker-status">${pub.isComplete ? '✓' : pending ? '…' : '○'}</div>
      `;

      // Popup content is the same PubRow as the pubs tab, portalled in below
      const popupNode = document.createElement('div');
      slots.push({ pub, index, node: popupNode });

      // Create marker
      const marker = new mapboxgl.Marker(markerElement)
        .setLngLat(coordinates)
        .setPopup(
          new mapboxgl.Popup({ offset: 25, maxWidth: '320px', className: pub.isComplete ? 'pub-popup pub-popup-done' : 'pub-popup' }).setDOMContent(popupNode)
        );

      if (map.current) {
        marker.addTo(map.current);
      }

      markers.current.push(marker);
    });
    setPopupSlots(slots);

    // Pubs that arrive after the map was created get one instant framing
    const bounds = getCrawlBounds(pubs);
    if (!framed.current && bounds) {
      map.current.fitBounds(bounds, { ...FIT_OPTIONS, animate: false });
      framed.current = true;
    }
  }, [pubs, pendingPubIds, mapLoaded]);

  if (mapError) {
    return (
      <div className="map-container">
        <div className="map-error">
          <h3>🗺️ Map Unavailable</h3>
          <p>{mapError}</p>
          <div className="pub-list-fallback">
            <h4>Pubs in this session:</h4>
            <ul>
              {pubs.map((pub, index) => (
                <li key={pub.id}>
                  {index + 1}. {pub.name}
                  {pub.isComplete && <span className="completed-indicator"> ✓</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="map-container">
      <div ref={mapContainer} className="mapbox-map" />
      {popupSlots.map(({ pub, index, node }) =>
        createPortal(
          <PubRow
            pub={pub}
            index={index}
            as="div"
            className="map-popup"
            testId="map-popup"
            pending={pendingPubIds.includes(pub.id)}
          />,
          node,
          pub.id
        )
      )}
    </div>
  );
};

export default Map;
